import './process-stats.css'

import { useEffect, useMemo, useState } from 'react'
import ReactJson from 'react-json-view'
import { useNavigate, useParams } from 'react-router-dom'

import {
  LineChartComponent,
  PageHeader,
  PieChartComponent,
  Spinner,
  Text
} from '../../../Components'
import { useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { getStepStatusVariant } from '../../../utils/statusVariants'
import { STATE_ENUM } from '../../Gigi/GigiList/Functions'
import { formatTime } from '../processFormatters'
import { Pricing } from './Pricing'

const language = cap.language.state || 'IT'
const t = translations[language]

const countStepStates = (processSteps) => {
  const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 }
  for (const item of processSteps) {
    if (item.stepState in counts) {
      counts[item.stepState]++
    }
  }
  return counts
}

/**
 * Formatta una durata in millisecondi per la UI statistiche.
 * @param {number} ms - Durata in millisecondi
 * @returns {string} Testo leggibile (es. "12 secondi" o "1 minuti e 3.45 secondi")
 */
const formatDurationMs = (ms) => {
  if (!ms) return `0 ${t.secondi}`
  if (ms < 60000) {
    const seconds = Math.floor(ms / 1000)
    return `${seconds} ${t.secondi}`
  }
  const minutes = Math.floor(ms / 60000)
  const remainingSeconds = ((ms % 60000) / 1000).toFixed(2)
  return `${minutes} ${t.minuti} ${t.eWord} ${remainingSeconds} ${t.secondi}`
}

/**
 * Calcola media, min, max e somma dei tempi di esecuzione degli step.
 * @param {Array} processes - Lista process step
 * @returns {{ avg: string, min: string, max: string, sum: string }} Statistiche formattate
 */
const calculateExecutionTimeStats = (processes) => {
  const times = processes.map((p) => p.executionTime).filter((t) => t > 0)
  if (!times.length) {
    return {
      avg: `0 ${t.secondi}`,
      min: `0 ${t.secondi}`,
      max: `0 ${t.secondi}`,
      sum: `0 ${t.secondi}`
    }
  }
  const sumMs = times.reduce((acc, t) => acc + t, 0)
  const minMs = Math.min(...times)
  const maxMs = Math.max(...times)
  const avgMs = sumMs / times.length
  return {
    avg: formatDurationMs(avgMs),
    min: formatDurationMs(minMs),
    max: formatDurationMs(maxMs),
    sum: formatDurationMs(sumMs)
  }
}

/**
 * Ordina gli step per ordine di esecuzione (stepIndex, poi id).
 * @param {Array} steps - Lista process step
 * @returns {Array} Step ordinati
 */
const sortByExecutionOrder = (steps) =>
  [...steps].sort((a, b) => {
    const indexDiff = (a.stepIndex ?? 0) - (b.stepIndex ?? 0)
    return indexDiff !== 0 ? indexDiff : a.id - b.id
  })

/**
 * Ordina gli step per durata decrescente.
 * @param {Array} steps - Lista process step
 * @returns {Array} Step ordinati
 */
const sortByDuration = (steps) =>
  [...steps].sort((a, b) => (b.executionTime || 0) - (a.executionTime || 0))

const COLORS = ['red', 'orange']
const timeDataKeys = [{ dataKey: 'Time', color: 'red' }]

/**
 * Calcola la percentuale di completamento per la barra di progresso.
 * @param {number} value - Conteggio stato
 * @param {number} total - Totale step
 * @returns {number} Percentuale 0-100
 */
function getProgressPercent(value, total) {
  if (!total) return 0
  return Math.round((value / total) * 100)
}

/**
 * Pagina statistiche degli step di un processo.
 * @returns {JSX.Element} Componente statistiche processo
 */
export function ProcessStepsStats() {
  const { processId } = useParams()
  const { data: processSteps = [], isSuccess } = useCQuery(['processSteps', processId])
  const navigate = useNavigate()
  const [stepStateCounts, setStepStateCounts] = useState([])
  const [executionTimeStats, setExecutionTimeStats] = useState({
    avg: `0 ${t.secondi}`,
    min: `0 ${t.secondi}`,
    max: `0 ${t.secondi}`,
    sum: `0 ${t.secondi}`
  })
  const [errorProcessSteps, setErrorProcessSteps] = useState([])
  const [dataExecutionTimes, setDataExecutionTimes] = useState([])
  const [unitSort, setUnitSort] = useState('execution')

  useEffect(() => {
    if (isSuccess) {
      const stepStateCounts = countStepStates(processSteps)
      const stats = calculateExecutionTimeStats(processSteps)
      const errorProcessSteps = processSteps.filter((item) => item.stepState === 5)
      const dataExecutionTimes = processSteps.map((step) => ({
        name: step.unit,
        Time: step.executionTime
      }))
      setStepStateCounts(stepStateCounts)
      setExecutionTimeStats(stats)
      setErrorProcessSteps(errorProcessSteps)
      setDataExecutionTimes(dataExecutionTimes)
    }
  }, [processSteps, isSuccess])

  const sortedProcessSteps = useMemo(() => {
    if (unitSort === 'duration') return sortByDuration(processSteps)
    return sortByExecutionOrder(processSteps)
  }, [processSteps, unitSort])

  if (!isSuccess) return <Spinner />

  const hasErrors = errorProcessSteps.length > 0
  const dataPieChart = [{ name: t.errore, value: errorProcessSteps.length }]
  const conversationId = processSteps[0].initialState.conversationData.conversationId
  const maxExecutionTime = Math.max(...processSteps.map((s) => s.executionTime || 0), 1)

  return (
    <div className="process-stats-page">
      <PageHeader
        title={t.processStats}
        subtitle={`${t.processo} #${processId}`}
        actions={
          <button
            type="button"
            className="nbtn"
            onClick={() => navigate('/conversations/' + conversationId)}
          >
            <i className="pi pi-chevron-left" style={{ fontSize: 14 }} />
            {t.tornaIndietro}
          </button>
        }
      />

      <div className="ngrid-2 process-stats-grid">
        <div className="process-stats-col">
          <div className="ncard">
            <div className="ncard__head">
              <div className="ncard__ico">
                <i className="pi pi-chart-bar" style={{ fontSize: 16 }} />
              </div>
              <div>
                <h3 className="ncard__title">{t.statoProcessSteps}</h3>
              </div>
            </div>
            <div className="ncard__body">
              {Object.entries(stepStateCounts).map(([key, value]) => {
                const stateEntry = STATE_ENUM.find((e) => e.value === Number(key))
                const percent = getProgressPercent(value, processSteps.length)
                return (
                  <div key={key} className="process-stats-progress-row">
                    <div className="process-stats-progress-row__head">
                      <span>{stateEntry?.label || key}</span>
                      <span className="ntable__num">
                        {value}/{processSteps.length}
                      </span>
                    </div>
                    <div className="process-stats-progress-bar">
                      <div
                        className="process-stats-progress-bar__fill"
                        style={{
                          width: `${percent}%`,
                          background:
                            stateEntry?.color === 'red'
                              ? 'var(--danger)'
                              : stateEntry?.color === 'green'
                                ? 'var(--success)'
                                : 'var(--primary)'
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {hasErrors ? (
            <div className="ngrid-2 process-stats-errors" style={{ marginTop: 16 }}>
              <div className="ncard">
                <div className="ncard__head">
                  <div className="ncard__ico">
                    <i className="pi pi-exclamation-circle" style={{ fontSize: 16 }} />
                  </div>
                  <div>
                    <h3 className="ncard__title">{t.errori}</h3>
                  </div>
                </div>
                <div className="ncard__body process-stats-errors__chart">
                  <PieChartComponent
                    legend
                    data={dataPieChart}
                    dataKey="value"
                    colors={COLORS}
                    innerRadius="0%"
                    outerRadius="100%"
                    paddingAngle="0"
                  />
                </div>
              </div>

              <div className="ncard">
                <div className="ncard__head">
                  <div className="ncard__ico">
                    <i className="pi pi-times-circle" style={{ fontSize: 16 }} />
                  </div>
                  <div>
                    <h3 className="ncard__title">{t.processStepsInErrore}</h3>
                  </div>
                </div>
                <div className="ncard__body">
                  {errorProcessSteps.map((error) => (
                    <div key={error.id || error.unit} style={{ marginBottom: 12 }}>
                      <Text bold value={error.unit} />
                      <div className="process-stats-json-wrap">
                        <ReactJson
                          name={t.errore}
                          onAdd={false}
                          collapsed
                          sortKeys
                          src={error.logStep}
                          displayObjectSize={false}
                          displayDataTypes={false}
                          onEdit={false}
                          onDelete={false}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          <div className="ncard" style={{ marginTop: 16 }}>
            <div className="ncard__head">
              <div className="ncard__ico">
                <i className="pi pi-euro" style={{ fontSize: 16 }} />
              </div>
              <div>
                <h3 className="ncard__title">{t.riepilogoCosti}</h3>
              </div>
            </div>
            <div className="ncard__body" style={{ padding: 0 }}>
              <Pricing />
            </div>
          </div>

          <div className="ncard" style={{ marginTop: 16 }}>
            <div className="ncard__head">
              <div className="ncard__ico">
                <i className="pi pi-chart-line" style={{ fontSize: 16 }} />
              </div>
              <div>
                <h3 className="ncard__title">{t.tempiDiEsecuzione}</h3>
              </div>
            </div>
            <div className="ncard__body process-stats-linechart__body">
              <LineChartComponent
                data={dataExecutionTimes}
                dataKeys={timeDataKeys}
                xaxis="name"
                yaxis
                cartesian
                tooltipProps={{
                  content: ({ payload: [{ payload: { name = '', Time } = {} } = {}] = [] }) => (
                    <div>
                      <Text value={`${name}`} />
                      <Text value={`${t.tempoDiEsecuzione} ${formatTime(Time / 1000)}`} />
                    </div>
                  )
                }}
                yAxisProps={{
                  type: 'number',
                  dataKey: 'Time',
                  domain: [0, 'dataMax + 20000'],
                  tickFormatter: (value) => formatTime(value / 1000, true)
                }}
              />
            </div>
          </div>
        </div>

        <div className="ncard">
          <div className="ncard__head process-stats-unit-times__head">
            <div className="process-stats-unit-times__head-left">
              <div className="ncard__ico">
                <i className="pi pi-clock" style={{ fontSize: 16 }} />
              </div>
              <div>
                <h3 className="ncard__title">{t.tempiUnit}</h3>
                <div className="process-stats-unit-times__stats">
                  <span className="ncard__sub">
                    {t.media}: {executionTimeStats.avg}
                  </span>
                  <span className="ncard__sub">
                    {t.min}: {executionTimeStats.min}
                  </span>
                  <span className="ncard__sub">
                    {t.max}: {executionTimeStats.max}
                  </span>
                  <span className="ncard__sub">
                    {t.sommaUnit} {executionTimeStats.sum}
                  </span>
                </div>
              </div>
            </div>
            <div className="process-stats-unit-times__sort">
              <button
                type="button"
                className={`npill ${unitSort === 'execution' ? 'is-active' : ''}`}
                onClick={() => setUnitSort('execution')}
              >
                {t.ordineEsecuzione}
              </button>
              <button
                type="button"
                className={`npill ${unitSort === 'duration' ? 'is-active' : ''}`}
                onClick={() => setUnitSort('duration')}
              >
                {t.durata}
              </button>
            </div>
          </div>
          <div className="ncard__body process-stats-bars">
            {sortedProcessSteps.map((process) => (
              <UnitTimingBar key={process.id} process={process} maxTime={maxExecutionTime} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Barra orizzontale CSS per il tempo di esecuzione di una unit.
 * @param {Object} props - Proprietà componente
 * @param {Object} props.process - Step processo con unit e executionTime
 * @param {number} props.maxTime - Tempo massimo per scala barra
 * @returns {JSX.Element} Barra timing unit
 */
function UnitTimingBar({ process, maxTime }) {
  const { unit, stepState, executionTime } = process
  const stateEntry = STATE_ENUM.find((e) => e.value === stepState)
  const width = executionTime ? Math.max(4, (executionTime / maxTime) * 100) : 0
  const barColor =
    stepState === 5 ? 'var(--danger)' : stepState === 3 ? 'var(--success)' : 'var(--primary)'

  return (
    <div className="process-stats-unit-bar">
      <div className="process-stats-unit-bar__head">
        <span className="process-stats-unit-bar__name">{unit}</span>
        <span className="process-stats-unit-bar__meta">
          <span className={`nora-status nora-status--${getStepStatusVariant(stepState)}`}>
            <span className="nora-status__dot" />
            <span className="nora-status__label">{stateEntry?.label}</span>
          </span>
          <span className="ntable__num">
            {executionTime ? formatTime(executionTime / 1000) : t.nonTerminato}
          </span>
        </span>
      </div>
      <div className="process-stats-progress-bar">
        <div
          className="process-stats-progress-bar__fill"
          style={{ width: `${width}%`, background: barColor }}
        />
      </div>
    </div>
  )
}
